import { Box, Button } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { ErrorMessage } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useLoadMoreIfEndOfScreen } from "@outschool/ui-utils";
import React from "react";

import { TextFeedbackByUserQueryVariables } from "../../../shared/generated/graphql";
import { usePublicReviewsByParentQuery } from "../../queries/PublicReviewsByParentQuery";
import { Review } from "../categories/curated/CuratedReviews";

export const PublicReviewsByParentInfiniteScroll = ({
  parentUid
}: {
  parentUid: string;
}) => {
  const { t } = useTranslation(
    "client\\components\\reviews\\PublicReviewsByParentInfiniteScroll"
  );
  const options: TextFeedbackByUserQueryVariables = {
    userUid: parentUid,
    offset: 0
  };

  const {
    data,
    error,
    loading,
    fetchMorePublicReviewsByParent,
    isFetchingMore
  } = usePublicReviewsByParentQuery({ variables: options });

  const [endOfScreenRef, setEndOfScreenRef] = React.useState<HTMLDivElement>();
  useLoadMoreIfEndOfScreen({
    endOfScreenRef,
    fetchMore: fetchMorePublicReviewsByParent,
    isInfiniteScrollEnabled: true
  });

  /** handle refetch and refetch errors */
  const [refetchError, setRefetchError] = React.useState<string | null>(null);
  const handleFetchMoreReviews = React.useCallback(async () => {
    try {
      await fetchMorePublicReviewsByParent();
    } catch (e) {
      setRefetchError(e);
    }
  }, [fetchMorePublicReviewsByParent]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {data?.textFeedbackByUser.map(review => (
        <Review
          review={review}
          showGrayCard={true}
          useShowMore={true}
          width={"100%"}
        />
      ))}

      <Box
        ref={setEndOfScreenRef}
        sx={{
          height: "10px"
        }}
      />

      {/* if there's an error, give the user the option to manually fetch more reviews */}
      {(error || refetchError) && (
        <>
          <ErrorMessage
            value={t("Oops, we encountered an error when loading reviews.")}
          />
          <Box
            flex
            sx={{
              justifyContent: "center"
            }}
          >
            <Button onClick={handleFetchMoreReviews}>
              {t("Try loading more reviews again")}
            </Button>
          </Box>
        </>
      )}

      {isFetchingMore && (
        <Box
          sx={{
            paddingBottom: "1em"
          }}
        >
          <Loading delay={0} />
        </Box>
      )}
    </>
  );
};
